import apiClient from './apiClient';

// Platform-level view of an organisation (tenant).
// Endpoint: GET /api/platform/organisations, GET /api/platform/organisations/switchable
export interface OrganisationDto {
	id: number;
	name: string;
	description?: string | null;
	isActive: boolean;
}

export interface CreateOrganisationRequest {
	name: string;
	description?: string | null;
}

// Update organisation request body (rename and/or activate/deactivate)
export interface UpdateOrganisationRequest {
	name?: string;
	description?: string | null;
	isActive?: boolean;
}

// A user belonging to an organisation (provision-admin picker)
export interface OrganisationUserDto {
	id: number;
	fullName: string;
	email: string;
	isOrganisationAdmin: boolean;
	// Names of the roles assigned to the user (e.g. "Admin", "Planner", "OrganisationAdmin")
	roleNames: string[];
}

// Optional filters for the organisation list. Omitting both returns every organisation, active and inactive.
export interface GetOrganisationsParams {
	search?: string;
	isActive?: boolean;
}

/**
 * Get organisations (platform organisation management screen), optionally filtered by search and status
 * Endpoint: GET /api/platform/organisations
 */
export async function getOrganisations(params: GetOrganisationsParams = {}): Promise<OrganisationDto[]> {
	const response = await apiClient.get<OrganisationDto[]>('/api/platform/organisations', { params });
	return response.data;
}

/**
 * Get a lightweight organisation list for the GlobalAdmin org-switcher dropdown
 * Endpoint: GET /api/platform/organisations/switchable
 */
export async function getSwitchableOrganisations(): Promise<OrganisationDto[]> {
	const response = await apiClient.get<OrganisationDto[]>('/api/platform/organisations/switchable');
	return response.data;
}

/**
 * Onboard a new organisation (tenant)
 * Endpoint: POST /api/platform/organisations
 * @returns The ID of the created organisation
 */
export async function createOrganisation(data: CreateOrganisationRequest): Promise<number> {
	const response = await apiClient.post<number>('/api/platform/organisations', data);
	return response.data;
}

/**
 * Rename and/or activate/deactivate an organisation
 * Endpoint: PATCH /api/platform/organisations/{id}
 * @returns The ID of the updated organisation
 */
export async function updateOrganisation(id: number, data: UpdateOrganisationRequest): Promise<number> {
	const response = await apiClient.patch<number>(`/api/platform/organisations/${id}`, data);
	return response.data;
}

/**
 * Get the users belonging to an organisation (provision-admin picker)
 * Endpoint: GET /api/platform/organisations/{id}/users
 */
export async function getOrganisationUsers(id: number): Promise<OrganisationUserDto[]> {
	const response = await apiClient.get<OrganisationUserDto[]>(`/api/platform/organisations/${id}/users`);
	return response.data;
}
